import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaVideo, FaStopCircle, FaDownload, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { RiRecordCircleFill } from 'react-icons/ri';

const ScreenRecorder = ({ isExpandedWindow, customBranding, color }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [downloadLink, setDownloadLink] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Cleanup resources when component unmounts
  useEffect(() => {
    return () => {
      stopAllStreams();
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (downloadLink) {
        URL.revokeObjectURL(downloadLink);
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [downloadLink]);
  
  // Stop all media streams
  const stopAllStreams = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
  };
  
  const startRecording = useCallback(async () => {
    try {
      // Reset state
      setRecordedChunks([]);
      setRecordingTime(0);
      setDownloadLink(null);
      
      // First, get the display media (screen) stream with system audio
      console.log("Requesting screen capture with audio...");
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: "always"
        },
        audio: true  // Request system audio - browser will show "Share audio" checkbox
      });
      streamRef.current = displayStream;
      
      // Set up audio context for mixing audio streams
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Create a destination for the mixed audio
      const audioDestination = audioContext.createMediaStreamDestination();
      
      // Create audio source from system audio if available
      const hasSystemAudio = displayStream.getAudioTracks().length > 0;
      if (hasSystemAudio) {
        console.log("System audio detected, adding to mix...");
        const systemSource = audioContext.createMediaStreamSource(new MediaStream([
          displayStream.getAudioTracks()[0]
        ]));
        systemSource.connect(audioDestination);
      } else {
        console.log("No system audio detected");
      }
      
      // Add microphone audio if enabled
      let micStream = null;
      if (micEnabled) {
        try {
          console.log("Requesting microphone access...");
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          });
          micStreamRef.current = micStream;
          
          // Create audio source from microphone
          const micSource = audioContext.createMediaStreamSource(micStream);
          micSource.connect(audioDestination);
          console.log("Microphone added to audio mix");
        } catch (err) {
          console.warn("Could not get microphone access:", err);
          // Continue without microphone
        }
      }
      
      // Create a new stream with video from screen and mixed audio
      const combinedStreamTracks = [
        ...displayStream.getVideoTracks(),
        ...audioDestination.stream.getAudioTracks()
      ];
      
      const combinedStream = new MediaStream(combinedStreamTracks);
      
      // Log track information for debugging
      console.log("Combined stream tracks:");
      combinedStream.getTracks().forEach(track => {
        console.log(`- ${track.kind}: ${track.label} (${track.enabled ? 'enabled' : 'disabled'})`);
      });
      
      // Create media recorder
      let options = {};
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
        options.mimeType = 'video/webm;codecs=vp9,opus';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
        options.mimeType = 'video/webm;codecs=vp8,opus';
      } else {
        options.mimeType = 'video/webm';
      }
      
      console.log(`Using MIME type: ${options.mimeType}`);
      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;
      
      // Set up data handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log(`Received chunk: ${event.data.size} bytes`);
          setRecordedChunks(prev => [...prev, event.data]);
        }
      };
      
      // Handle recording stop
      mediaRecorder.onstop = () => {
        console.log("Recording stopped");
        
        // Stop all streams
        stopAllStreams();
        
        // Close audio context
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        
        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        setIsRecording(false);
        setShowControls(true);
      };
      
      // Start recording
      mediaRecorder.start(1000);
      console.log("Recording started");
      setIsRecording(true);
      
      // Start recording timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);
      
      // Handle screen sharing end (user cancels)
      displayStream.getVideoTracks()[0].onended = () => {
        console.log("Screen sharing ended by user");
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording();
        }
      };
      
    } catch (error) {
      console.error("Error starting recording:", error);
      stopAllStreams();
      
      if (error.name === 'NotAllowedError') {
        alert('Permission denied. Please allow access to continue.');
      } else {
        alert(`Error starting recording: ${error.message}`);
      }
      
      setIsRecording(false);
    }
  }, [micEnabled]);
  
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log("Stopping recording...");
      mediaRecorderRef.current.stop();
    }
  }, []);
  
  const toggleMicrophone = useCallback(() => {
    setMicEnabled(!micEnabled);
  }, [micEnabled]);
  
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  const createVideoPreview = useCallback(() => {
    if (recordedChunks.length === 0) return null;
    
    try {
      console.log(`Creating preview from ${recordedChunks.length} chunks...`);
      const blob = new Blob(recordedChunks, {
        type: 'video/webm'
      });
      
      const url = URL.createObjectURL(blob);
      setDownloadLink(url);
      console.log("Preview created successfully");
      return url;
    } catch (error) {
      console.error("Error creating preview:", error);
      alert('Error creating preview. See console for details.');
      return null;
    }
  }, [recordedChunks]);
  
  const downloadRecording = useCallback(() => {
    if (recordedChunks.length === 0) return;
    
    try {
      console.log("Preparing download...");
      const blob = new Blob(recordedChunks, {
        type: 'video/webm'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      
      // Format filename with date/time
      const date = new Date();
      const formattedDate = date.toISOString().split('T')[0];
      const formattedTime = date.toTimeString().split(' ')[0].replace(/:/g, '-');
      
      a.href = url;
      a.download = `screen-recording-${formattedDate}-${formattedTime}.webm`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      console.log("Download initiated");
    } catch (error) {
      console.error("Error downloading recording:", error);
      alert('Error downloading recording. See console for details.');
    }
  }, [recordedChunks]);
  
  return (
    <div className="relative">
      {/* Main recording controls */}
      <div className="flex space-x-2">
        {/* Record/stop button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => {
            if (isRecording) {
              stopRecording();
            } else if (showControls) {
              setShowControls(false);
            } else {
              startRecording();
            }
          }}
          className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-300"
          title={isRecording ? "Stop Recording" : "Record Screen"}
          style={{ color: isRecording ? "#f87171" : (color || customBranding.headerTextColor) }}
        >
          {isRecording ? (
            <FaStopCircle size={18} />
          ) : (
            <FaVideo size={18} />
          )}
        </motion.button>
        
        {/* Microphone toggle button - Commented out as mic is always enabled
        {!isRecording && !showControls && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleMicrophone}
            className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-300"
            title={micEnabled ? "Disable Microphone" : "Enable Microphone"}
            style={{ color: color || customBranding.headerTextColor }}
          >
            {micEnabled ? (
              <FaMicrophone size={16} />
            ) : (
              <FaMicrophoneSlash size={16} />
            )}
          </motion.button>
        )}
        */}
      </div>
      
      {/* Recording timer indicator */}
      {isRecording && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-2 py-1 rounded-full text-xs"
        >
          <RiRecordCircleFill className="mr-1 animate-pulse" />
          <span>{formatTime(recordingTime)}</span>
        </motion.div>
      )}
      
      {/* Preview and download panel */}
      <AnimatePresence>
        {showControls && recordedChunks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={`absolute ${isExpandedWindow ? 'top-full' : 'right-0 top-full'} mt-2 p-4 bg-white shadow-lg rounded-lg z-50 w-64`}
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-gray-800">Recording Ready</h3>
              <button 
                onClick={() => setShowControls(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
            </div>
            
            {/* Video preview */}
            {downloadLink && (
              <video
                src={downloadLink}
                controls
                className="w-full h-auto rounded mb-2"
              />
            )}
            
            {/* Action buttons */}
            <div className="flex space-x-2 mb-2">
              {!downloadLink && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={createVideoPreview}
                  className="flex items-center justify-center bg-gray-100 text-gray-800 px-3 py-2 rounded w-full text-sm"
                >
                  <FaVideo className="mr-2" /> Preview
                </motion.button>
              )}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={downloadRecording}
                className="flex items-center justify-center bg-blue-500 text-white px-3 py-2 rounded w-full text-sm"
              >
                <FaDownload className="mr-2" /> Download
              </motion.button>
            </div>
            
            {/* Recording info */}
            <div className="text-xs text-center text-gray-500">
              {recordedChunks.length > 0 && 
                `Recording: ${formatTime(recordingTime)} - ${(recordedChunks.reduce((total, chunk) => total + chunk.size, 0) / (1024 * 1024)).toFixed(2)} MB`}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ScreenRecorder;